import React, { useState } from 'react';
import { MessageSquare, Loader } from 'lucide-react';
import Modal from './Modal';
import { feedback as feedbackApi } from '../../utils/api';

const FeedbackModal = ({ isOpen, onClose }) => {
  const [feedbackType, setFeedbackType] = useState(null);
  const [description, setDescription] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const feedbackTypes = [
    {
      id: 'bug',
      label: 'Bug',
      description: 'Something isn\'t working as expected',
      icon: '🐛'
    },
    {
      id: 'idea',
      label: 'Feature Idea',
      description: 'Suggest a new feature or improvement',
      icon: '💡'
    },
    {
      id: 'feedback',
      label: 'General Feedback',
      description: 'Any other comments or suggestions',
      icon: '💬'
    }
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!feedbackType || !description.trim()) {
      alert('Please select a feedback type and provide a description');
      return;
    }

    setLoading(true);
    setSubmitStatus(null);

    try {
      await feedbackApi.submit({
        type: feedbackType,
        description: description.trim()
      });

      setSubmitStatus('success');

      // Reset form after success
      setTimeout(() => {
        setFeedbackType(null);
        setDescription('');
        setSubmitStatus(null);
        onClose();
      }, 1500);
    } catch (error) {
      console.error('Failed to submit feedback:', error);
      setSubmitStatus('error');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFeedbackType(null);
    setDescription('');
    setSubmitStatus(null);
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Share Your Feedback"
      size="lg"
      closeOnBackdropClick={!loading}
    >
      <div id="feedback-modal-content">
        {submitStatus === 'success' ? (
          <div id="feedback-modal-success" className="text-center py-8">
            <div className="mb-4 text-4xl">✨</div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              Thank you for your feedback!
            </h3>
            <p className="text-gray-600">
              We've received your message and will review it shortly.
            </p>
          </div>
        ) : submitStatus === 'error' ? (
          <div id="feedback-modal-error" className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
            <p className="text-red-700 font-medium">
              Something went wrong. Please try again later.
            </p>
          </div>
        ) : null}

        {submitStatus !== 'success' && (
          <form onSubmit={handleSubmit} id="feedback-modal-form">
            {/* Feedback Type Selection */}
            <div id="feedback-modal-type-selection" className="mb-6">
              <label className="block text-sm font-medium text-gray-900 mb-3">
                What type of feedback?
              </label>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                {feedbackTypes.map((type) => (
                  <button
                    key={type.id}
                    type="button"
                    onClick={() => setFeedbackType(type.id)}
                    id={`feedback-type-${type.id}`}
                    className={`p-4 rounded-lg border-2 transition-all text-left ${
                      feedbackType === type.id
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 bg-white hover:border-gray-300'
                    }`}
                  >
                    <div className="text-2xl mb-2">{type.icon}</div>
                    <h4 className="font-medium text-gray-900">{type.label}</h4>
                    <p className="text-sm text-gray-600 mt-1">{type.description}</p>
                  </button>
                ))}
              </div>
            </div>

            {/* Description */}
            <div id="feedback-modal-description" className="mb-6">
              <label
                htmlFor="feedback-description"
                className="block text-sm font-medium text-gray-900 mb-2"
              >
                Tell us more
              </label>
              <textarea
                id="feedback-description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Please provide as much detail as possible. Include steps to reproduce if it's a bug, or explain your feature request..."
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none resize-none"
                rows="6"
                disabled={loading}
              />
              <p className="text-xs text-gray-500 mt-2">
                {description.length} / 5000 characters
              </p>
            </div>

            {/* Buttons */}
            <div id="feedback-modal-actions" className="flex gap-3 justify-end">
              <button
                type="button"
                onClick={handleClose}
                disabled={loading}
                className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                id="feedback-modal-cancel-button"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading || !feedbackType || !description.trim()}
                className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 font-medium"
                id="feedback-modal-submit-button"
              >
                {loading ? (
                  <>
                    <Loader className="w-4 h-4 animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    <MessageSquare className="w-4 h-4" />
                    Send Feedback
                  </>
                )}
              </button>
            </div>
          </form>
        )}
      </div>
    </Modal>
  );
};

export default FeedbackModal;
